import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyAssignedEmployees } from "api/apiHelper";
import type { ClientAssignment } from "types/client-management-types";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import Table from "components/Table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const AssignedEmployeesTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ClientAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [paginationData, setPaginationData] = useState<PaginatedResponse<ClientAssignment> | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const columnHelper = createColumnHelper<ClientAssignment>();

  const handleRowClick = async (row: ClientAssignment) => {
    // Try to get employee_id from row, otherwise fetch assignment detail
    let employeeId = row.employee_id || row.employee;
    
    if (!employeeId) {
      // Fetch assignment detail to get employee_id
      try {
        const { getAssignmentDetail } = await import("api/apiHelper");
        const response = await getAssignmentDetail(row.id);
        employeeId = response.data?.employee_id || response.data?.employee?.id || response.data?.employee;
      } catch (err) {
        console.error("Failed to fetch assignment detail:", err);
        // If we can't get employee_id, we can't navigate
        return;
      }
    }
    
    if (employeeId) {
      navigate(`/client-portal/employee/${employeeId}`);
    }
  };

  const columns: ColumnDef<ClientAssignment, any>[] = [
    columnHelper.accessor("employee_name", {
      id: "employee_name",
      header: () => <TableHeader>Employee</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="font-medium text-gray-900 dark:text-white">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("employee_email", {
      id: "employee_email",
      header: () => <TableHeader>Email</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-700 dark:text-gray-300">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("client_name", {
      id: "client_name",
      header: () => <TableHeader>Client</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-700 dark:text-gray-300">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("client_user_name", {
      id: "client_user_name",
      header: () => <TableHeader>Assigned By</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-700 dark:text-gray-300">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <TableHeader>Assigned On</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-500 dark:text-gray-400">
            {new Date(info.getValue()).toLocaleDateString()}
          </span>
        </TableCell>
      ),
    }),
  ];

  const fetchAssignments = async (size: number = pageSize, pageUrl?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMyAssignedEmployees(searchTerm, size, pageUrl);
      
      if (response.data?.results) {
        // Paginated response
        setPaginationData(response.data);
        setData(response.data.results);
      } else if (Array.isArray(response.data)) {
        // Non-paginated response
        setData(response.data);
        setPaginationData(null);
      } else {
        setData([]);
        setPaginationData(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load assigned employees.");
      setData([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = async (newSize: number) => {
    setPageSize(newSize);
    await fetchAssignments(newSize);
  };

  const handleNextPage = async () => {
    if (paginationData?.next) {
      await fetchAssignments(pageSize, paginationData.next);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = async () => {
    if (paginationData?.previous) {
      await fetchAssignments(pageSize, paginationData.previous);
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    await fetchAssignments(pageSize);
  };

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !data.length) return <Backdrop show={true} />;

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}
      
      {/* Search Bar */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Search by employee name, email, or client name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
        />
        <button
          onClick={handleSearch}
          className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium"
        >
          <i className="fas fa-search mr-2"></i>
          Search
        </button>
      </div>

      <Table
        tableData={data}
        columns={columns}
        tableHeading="Assigned Employees"
        totalCount={paginationData?.count}
        currentPage={currentPage}
        pageSize={pageSize}
        hasNextPage={!!paginationData?.next}
        hasPreviousPage={!!paginationData?.previous}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default AssignedEmployeesTable;
